import React from 'react'
import cn from 'classnames'
import { Table as AntTable } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { TableWrapper } from './table/Wrapper'
import { TableSortHandler } from './table/SortHandler'
import { TableSortable } from './table/Sortable'
import { TableSortableItem } from './table/SortableItem'
import { TableActions, TableActionsProps } from './table/Actions'
import { observer } from 'mobx-react'
import styles from './Table.module.scss'

interface Props<T> {
  idField: keyof T & string
  items: T[]
  columns: ColumnsType<T>
  onSortRows?: (oldIndex: number, newIndex: number, nextItems: T[]) => void
  onRowClick?: (item: T) => void
  onRowDoubleClick?: (item: T) => void
  pagination?: IPaginationSettings | null
  actions?: (item: T) => TableActionsProps
  showHeader?: boolean
  className?: string
}

export const Table = observer(<T extends Object>({ idField, items, columns, onSortRows, onRowClick, onRowDoubleClick, pagination, actions, showHeader, className }: Props<T>) => {
  const antPagination = React.useMemo(() => {
    if (!pagination || pagination.total < pagination.pageSize) { return false }
    return {
      current: pagination.page,
      total: pagination.total,
      pageSize: pagination.pageSize,
      showSizeChanger: false,
      showQuickJumper: false,
      style: { marginRight: 8 },
      onChange: pagination.onChange,
    }
  }, [pagination])

  const onRow = React.useCallback((item: T) => ({
    onClick: () => onRowClick?.(item),
    onDoubleClick: () => onRowDoubleClick?.(item),
  }), [onRowClick, onRowDoubleClick])

  const antTableColumns = React.useMemo(() => {
    const result: ColumnsType<T> = []
    if (onSortRows) {
      result.push({
        key: 'handle',
        dataIndex: 'handle',
        width: '1%',
        fixed: 'left',
        render: (_: unknown, item: T) => <TableSortHandler id={item[idField] as unknown as string | number} />
      })
    }
    result.push(...columns)
    if (actions) {
      result.push({
        key: 'actions',
        dataIndex: 'actions',
        title: '',
        width: 50,
        fixed: 'right',
        render: (_, item) => (
          <TableActions {...actions(item)} />
        ),
      })
    }
    return result
  }, [columns, idField, onSortRows, actions])

  const antTableComponents = React.useMemo(() => {
    return onSortRows ? { body: { row: TableSortableItem } } : {}
  }, [onSortRows])

  const tableRendered = (
    <AntTable
      rowKey={idField}
      dataSource={items}
      columns={antTableColumns}
      size={'small'}
      tableLayout={'auto'}
      pagination={antPagination}
      components={antTableComponents}
      showHeader={showHeader}
      onRow={onRow}
    />
  )

  return (
    <div className={cn(styles.root, className)}>
      <TableWrapper>
        {
          onSortRows
            ? (
              <TableSortable items={items} idField={idField} onChange={onSortRows}>
                {tableRendered}
              </TableSortable>
            )
            : tableRendered
        }
      </TableWrapper>
    </div>
  )
})

export interface IPaginationSettings {
  page: number
  total: number
  pageSize: number
  onChange?: (page: number, pageSize: number) => void
}
